import React, { useState } from "react";
import { useContinentRegionContext } from "../../../../../context/continentRegion/context/ContinentRegionContext";
import {
  Title,
  GoBack,
  TextArea,
  InputField,
  MyMessage,
  MyActionButton,
  MyButton,
} from "../../../component/Component";
import { FOLDERS } from "../../../../doseeData/utils/Utils";
import { ImageKitUpload } from "../../../../../middlewares/libraries/imageKit/ImageKitLibrary";
import { defaultImage } from "../../../../../middlewares/libraries/imageKit/ImageKit";
import GenerateDescription from "../../../../doseeData/utils/GenerateDescription";
import { MdAutoFixHigh } from "react-icons/md";

const regionRoute = "/region/manage";
const addTilte = "Region Details";

const AddRegion = () => {
  const { addRegion } = useContinentRegionContext();

  const [name, setName] = useState(null);
  const [thumbnail, setThumbnail] = useState(defaultImage);
  const [thumbnailId, setThumbnailId] = useState("img_cdn_id");
  const [description, setDescription] = useState(null);
  const [numOfCountry, setNumOfCountry] = useState(null);
  const [error, setError] = useState(false);
  const [message, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [showDescriptionModal, setShowDescriptionModal] = useState(null);

  const cleanFields = () => {
    setLoading(false);
    setSuccess(true);
    setLoading(false);
    setThumbnail(null);
    setThumbnailId(null);
    setName(null);
    setNumOfCountry(null);
    setDescription(null);
  };

  const responses = (error, message) => {
    setError(error);
    setLoading(false);
    setSuccess(!error);
    setErrorMessage(message);
  };

  const sendAddRegionRequest = async (e) => {
    e.preventDefault();
    setLoading(true);
    if (!thumbnail || !thumbnailId) responses(true, "Image Required");
    else await sendDBRequest();
  };

  /**
   * @author Ntavigwa Bashombe
   * @param {*} thumbnail
   * @returns
   */
  const sendDBRequest = async () => {
    const data = {
      name: name,
      slug: name.toLowerCase().replace(/\s/g, "_"),
      continent_id: 1,
      thumbnail: thumbnail ? thumbnail : defaultImage,
      image_cdn_id: thumbnailId ? thumbnailId : "",
      description: description,
      country_number: numOfCountry,
    };

    const { status, message } = await addRegion(data);
    if (status === 200) {
      cleanFields();
      responses(false, message);
    } else {
      responses(true, message);
    }
  };

  const slugPreview =
    name && String(name).trim() !== ""
      ? String(name).toLowerCase().trim().replace(/\s+/g, "_")
      : "—";
  const descCount = description ? String(description).length : 0;

  return (
    <div className="p-4 lg:p-6 h-full overflow-y-auto rounded-b-xl bg-gray-50">
      <GoBack props={{ route: regionRoute }} />

      {showDescriptionModal ? (
        <GenerateDescription
          setDescription={setDescription}
          setShowDescriptionModal={setShowDescriptionModal}
        />
      ) : null}

      <form onSubmit={sendAddRegionRequest} className="max-w-5xl mx-auto">
        <div className="relative">
          <MyMessage
            error={error}
            message={message}
            success={success}
            onExpired={() => {
              setError(false);
              setSuccess(false);
            }}
            onClick={() => {
              setError(false);
              setSuccess(false);
              setErrorMessage("");
            }}
          />

          <div className="flex items-center justify-between mb-2">
            <Title props={{ title: addTilte }} />
          </div>

          {/* Intro helper */}
          <p className="mt-1 mb-5 text-[11px] text-gray-600">
            Add a region with a clear name, high-quality thumbnail, an accurate
            country count, and a compelling description. Your thumbnail should be
            landscape and under 1MB for best performance.
          </p>

          {/* Section: Image */}
          <section className="mb-6 rounded-xl border border-gray-200 bg-white p-4 lg:p-6 shadow-sm">
            <h3 className="text-sm font-semibold text-gray-900 mb-1">
              Region Thumbnail
            </h3>
            <p className="text-[11px] text-gray-600 mb-4">
              Upload a representative image for this region. Supported formats:
              JPG/PNG.
            </p>
            <ImageKitUpload
              thumbnail={thumbnail}
              thumbnailId={thumbnailId}
              setThumbnail={setThumbnail}
              setThumbnailId={setThumbnailId}
              folder={FOLDERS.CONTINENT_REGIONS}
            />
          </section>

          {/* Section: Basics */}
          <section className="mb-6 rounded-xl border border-gray-200 bg-white p-4 lg:p-6 shadow-sm">
            <h3 className="text-sm font-semibold text-gray-900 mb-4">
              Basic Information
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-2">
                <InputField
                  props={{
                    id: "name",
                    title: "Name",
                    type: "text",
                    required: true,
                    value: name ? name : "",
                    onChange: (e) => {
                      setName(e.target.value);
                      setError(null);
                      setSuccess(null);
                    },
                  }}
                />
                <div className="mt-1 flex items-center justify-between">
                  <span className="text-[11px] text-gray-500">
                    Slug preview: <span className="font-mono">{slugPreview}</span>
                  </span>
                  <span className="text-[11px] text-gray-400">
                    Tip: Keep names short and recognizable.
                  </span>
                </div>
              </div>

              <div>
                <InputField
                  props={{
                    id: "number_of_countries",
                    title: "Number of Countries",
                    type: "number",
                    required: true,
                    value: numOfCountry ? numOfCountry : "",
                    onChange: (e) => {
                      setNumOfCountry(e.target.value);
                      setError(null);
                      setSuccess(null);
                    },
                  }}
                />
                <p className="mt-1 text-[11px] text-gray-500">
                  Total countries included in this region.
                </p>
              </div>
            </div>
          </section>

          {/* Section: Description */}
          <section className="rounded-xl border border-gray-200 bg-white p-4 lg:p-6 shadow-sm">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-gray-900">
                Description
              </h3>
              <MyButton
                onClick={() => setShowDescriptionModal(true)}
                className="bg-blue-700 hover:bg-blue-600 py-2 px-3 rounded-lg text-white text-xs font-semibold inline-flex items-center gap-2"
              >
                <MdAutoFixHigh className="text-base" />
                Generate Description
              </MyButton>
            </div>
            <p className="mt-1 mb-3 text-[11px] text-gray-600">
              Highlight what makes this region unique—culture, landscapes,
              signature experiences, and travel tips.
            </p>

            <TextArea
              props={{
                id: "description",
                title: "Description",
                type: "text",
                value: description ? description : "",
                onChange: (e) => {
                  setDescription(e.target.value);
                  setError(null);
                  setSuccess(null);
                },
              }}
            />

            <div className="mt-2 flex items-center justify-between">
              <span className="text-[11px] text-gray-500">
                {descCount} characters
              </span>
              <span className="text-[11px] text-gray-400">
                Aim for a concise, informative overview.
              </span>
            </div>
          </section>

          {/* Actions */}
          <div className="flex justify-center pt-6">
            <MyActionButton
              loading={loading}
              title="Add Region"
              onClick={() => {
                setError(false);
                setSuccess(false);
                setErrorMessage(null);
              }}
            />
          </div>
        </div>
      </form>
    </div>
  );
};

export default AddRegion;
